/**
 * Sentence-completion archetype renderer (fix #252 / #PL-WEB-RUBRICS).
 *
 * Drag-from-bank into a single blank per slot. The kid sees a
 * scenario (image + audio) and hears `prompt_audio`. Three words
 * appear in the bank; the kid drags each into a slot in order.
 *
 * Per the issue body's Risks section: WebKit drag-and-drop is
 * unreliable on iOS Safari. The renderer supports both:
 *
 *   - HTML5 drag events (`dragstart`/`drop`) on browsers that
 *     fire them reliably (desktop Chrome, desktop Firefox).
 *   - A `tap-to-select` + `tap-to-place` fallback. Tap a bank
 *     word to "arm" it (highlighted), then tap an empty slot to
 *     place. Tap a filled slot to return the word to the bank.
 *
 * The tap path is the primary one — it works everywhere, and on
 * WebKit browsers that DO fire drag events the kid can still use
 * either interaction.
 *
 * Self-correction: drag back to bank + re-drag (or tap a filled
 * slot to return + tap an empty one to re-place). No time window
 * is enforced because the bank→blank→bank gesture is inherently
 * a correction.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { SentenceCompletionRubric } from '../../../lib/practice/rubrics';
import { scoreSentenceCompletion } from '../../../lib/practice/scoring';
import type { ArchetypePlayerProps, TrialOutcome } from './shared';
import { PhotoIcon } from '../../../components/icons/PhotoIcon';

interface Props extends ArchetypePlayerProps<SentenceCompletionRubric> {}

export default function SentenceCompletionPlayer({
  rubric,
  trialIndex,
  totalTrials,
  onTrialComplete,
}: Props): ReactElement {
  const initialState = {
    placements: Array.from<string | null>({ length: rubric.expected.length }).fill(null),
    bankRemaining: rubric.word_bank.map((_, i) => i),
  };
  const [state, setState] = useState(initialState);
  const [armedWord, setArmedWord] = useState<number | null>(null);
  const [startedAt, setStartedAt] = useState<number>(() => Date.now());
  // Self-correction channel: tap/drag a filled slot back to the bank
  // and re-place. Per `rubrics.ts` this gesture is the correction
  // signal for sentence_completion (no time window — the bank↔blank
  // round-trip IS the correction).
  const hasReturnedToBankRef = useRef(false);

  useEffect(() => {
    setStartedAt(Date.now());
    setState({
      placements: Array.from<string | null>({ length: rubric.expected.length }).fill(null),
      bankRemaining: rubric.word_bank.map((_, i) => i),
    });
    setArmedWord(null);
    hasReturnedToBankRef.current = false;
  }, [trialIndex, rubric]);

  function placeWord(slot: number, bankIdx: number): void {
    const armedText = rubric.word_bank[bankIdx] ?? null;
    if (!armedText) return;
    setState((prev) => {
      const currentlyInSlot = prev.placements[slot];
      if (currentlyInSlot === armedText) return prev;

      const nextPlacements = prev.placements.slice();
      nextPlacements[slot] = armedText;

      // Move the newly-placed word out of the bank.
      let nextBank = prev.bankRemaining.filter((i) => i !== bankIdx);

      // Return the word that was previously in this slot (if any).
      if (currentlyInSlot) {
        const oldIdx = rubric.word_bank.indexOf(currentlyInSlot);
        if (oldIdx >= 0 && !nextBank.includes(oldIdx)) {
          nextBank = [...nextBank, oldIdx].sort((a, b) => a - b);
        }
      }

      return { placements: nextPlacements, bankRemaining: nextBank };
    });
    setArmedWord(null);
  }

  function returnToBank(slot: number): void {
    // Only count as a self-correction if the slot actually held a word
    // at the time of the gesture. The state updater no-ops on an empty
    // slot, so we must guard the analytics flag the same way to avoid
    // over-reporting `self_corrected=true` for no-op gestures (#358).
    if (state.placements[slot] == null) return;
    setState((prev) => {
      const wordInSlot = prev.placements[slot];
      if (!wordInSlot) return prev;
      const idx = rubric.word_bank.indexOf(wordInSlot);
      const nextPlacements = prev.placements.slice();
      nextPlacements[slot] = null;
      const nextBank =
        idx >= 0 && !prev.bankRemaining.includes(idx)
          ? [...prev.bankRemaining, idx].sort((a, b) => a - b)
          : prev.bankRemaining;
      return { placements: nextPlacements, bankRemaining: nextBank };
    });
    setArmedWord(null);
    hasReturnedToBankRef.current = true;
  }

  function onBankTap(bankIdx: number): void {
    setArmedWord((prev) => (prev === bankIdx ? null : bankIdx));
  }

  function onSubmit(): void {
    const { placements } = state;
    const response = placements.map((p) => p ?? '');
    const score = scoreSentenceCompletion(response, rubric);
    const outcome: TrialOutcome = {
      isCorrect: score.is_correct,
      latencyMs: Date.now() - startedAt,
      selfCorrected: hasReturnedToBankRef.current,
      usedCue: false,
      response,
      expected: rubric.expected,
      promptKind: 'mixed',
    };
    onTrialComplete(outcome);
  }

  const placedCount = state.placements.filter((p): p is string => p !== null).length;
  const allPlaced = placedCount === rubric.expected.length;

  // Compute the outcome-as-of-the-current-placement so every
  // filled slot can show the kid a correct/incorrect cue the
  // instant they place a word (fix #421 — applying the same
  // immediate-reinforcement pattern as PictureNaming #403 to
  // the sibling archetype renderers). Per-slot scoring: word at
  // `placements[i]` is correct iff it matches `expected[i]` or
  // `alternates?.[i]` (case-insensitive, mirroring
  // `scoreSentenceCompletion`). The visual cue persists from
  // the place gesture until the kid advances; tapping a filled
  // slot to return-to-bank and re-placing re-evaluates the cue.
  // We deliberately surface right/wrong per slot rather than
  // hiding the outcome — sentence_completion is targeted at
  // kids old enough to process immediate correctness feedback,
  // which is the entire pedagogical point of a sentence-fill
  // trial.
  function slotIsCorrect(slotIdx: number): boolean | null {
    const placed = state.placements[slotIdx];
    if (placed == null) return null;
    const want = (rubric.expected[slotIdx] ?? '').toLowerCase();
    const alts = (rubric.alternates?.[slotIdx] ?? []).map((s) =>
      s.toLowerCase(),
    );
    const got = placed.toLowerCase();
    return got === want || alts.includes(got);
  }
  const slotCorrectCount = state.placements.reduce<number>(
    (acc, _p, i) => (slotIsCorrect(i) === true ? acc + 1 : acc),
    0,
  );
  const allSlotsCorrect =
    allPlaced && slotCorrectCount === rubric.expected.length;

  return (
    <main className="screen handoff">
      <div className="auth-hero">
        <header className="screen-header">
          <h1>Trial {trialIndex + 1} / {totalTrials}</h1>
          <p className="tagline">Drag the words to fill the sentence.</p>
        </header>
      </div>
      {rubric.scenario_image_url ? (
        <img
          src={rubric.scenario_image_url}
          alt="Scenario"
          className="prompt-image"
        />
      ) : (
        <div className="prompt-placeholder" aria-hidden="true">
          <PhotoIcon size={32} aria-label="Scenario placeholder" />
        </div>
      )}
      <audio
        controls
        src={rubric.scenario_audio}
        aria-label="Scenario audio"
      >
        <track kind="captions" />
      </audio>
      <p className="tagline">Drop your words below:</p>
      <div
        className="slot-row"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          justifyContent: 'center',
        }}
      >
        {state.placements.map((placed, slot) => {
          const slotCorrect = slotIsCorrect(slot);
          const outcomeClass =
            placed && slotCorrect === true
              ? ' selected correct'
              : placed && slotCorrect === false
                ? ' selected incorrect'
                : allPlaced && !placed
                  ? ' muted'
                  : '';
          return (
            <button
              type="button"
              key={`slot-${slot}`}
              className={`snap-button${outcomeClass}`}
              onClick={(): void => {
                if (placed) returnToBank(slot);
                else if (armedWord !== null) placeWord(slot, armedWord);
              }}
              onDragOver={(e): void => e.preventDefault()}
              onDrop={(e): void => {
                e.preventDefault();
                const raw = e.dataTransfer.getData('text/plain');
                const bankIdx = Number.parseInt(raw, 10);
                if (!Number.isNaN(bankIdx)) placeWord(slot, bankIdx);
              }}
              aria-label={
                placed && slotCorrect === true
                  ? `Slot ${slot + 1}: ${placed}, correct, tap to return to bank`
                  : placed && slotCorrect === false
                    ? `Slot ${slot + 1}: ${placed}, wrong; expected ${rubric.expected[slot] ?? 'unknown'}, tap to return to bank`
                    : placed
                      ? `Slot ${slot + 1}: ${placed}, tap to return to bank`
                      : armedWord !== null
                        ? `Empty slot, tap to place ${rubric.word_bank[armedWord]}`
                        : `Empty slot ${slot + 1}`
              }
              style={{ minWidth: '80px' }}
            >
              {placed ?? '___'}
            </button>
          );
        })}
      </div>
      <p className="tagline">Word bank:</p>
      <div
        className="bank-row"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          justifyContent: 'center',
        }}
      >
        {state.bankRemaining.map((bankIdx) => {
          const word = rubric.word_bank[bankIdx] ?? '';
          return (
            <button
              type="button"
              key={`bank-${bankIdx}`}
              className={`snap-button${armedWord === bankIdx ? ' selected' : ''}`}
              onClick={(): void => onBankTap(bankIdx)}
              draggable
              onDragStart={(e): void => {
                e.dataTransfer.setData('text/plain', String(bankIdx));
                e.dataTransfer.effectAllowed = 'move';
              }}
              aria-label={
                armedWord === bankIdx
                  ? `Word "${word}" armed, tap an empty slot to place`
                  : `Word "${word}", tap to arm`
              }
            >
              {word}
            </button>
          );
        })}
      </div>
      {armedWord !== null ? (
        <p className="tagline" role="status">
          Tap a slot to place &ldquo;{rubric.word_bank[armedWord]}&rdquo;
          {' '}or tap the word again to disarm.
        </p>
      ) : null}
      {allPlaced ? (
        <button
          type="button"
          className="snap-button"
          onClick={onSubmit}
          aria-label={
            allSlotsCorrect
              ? `Got it! — all ${slotCorrectCount} slots correct`
              : `Got it! — ${slotCorrectCount} of ${placedCount} slots correct`
          }
        >
          Got it!
        </button>
      ) : null}
    </main>
  );
}
